import ShortlistIcon from "icons/ShortlistIcon";
import styles from "./ShortListButton.module.scss";
import { useState, MouseEventHandler, useEffect } from "react";
import { AppState } from "@Store/index";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import LoginDispatcher from "@Components/Login/actions";
import themeVariables from "@Theme/exportColor.module.scss";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import createInventoriesShortlist, {
  deleteInventoriesShortlist,
  addShortlistParamInURL,
} from "./helper";
import LoaderIcon from "@Icons/LoaderIcon";
import WishlistHeartIcon from "@Icons/WishlistHeartIcon";
import BurstHeart, { BurstHeartAction } from "@Components/BurstHeart/BurstHeart";
import ToastNotificationDispatcher from "@Components/ToastNotification/actions";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { updateUserData } from "@App/appReducer";
import ActivityPageDispatcher from "@Pages/Activity/data/actions";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { getAvailableStartingPricingHelper } from "@Pages/BookForm/utils";
import { getPricingCurrencyStandardName } from "@Pages/PropertyPage/components/RoomTypeSection/utils";
import { logAppEvent } from "@Utils/appAnalytics";
import useIsApp, { useIsNewNativeApp } from "hooks/useIsApp";
import { APP_EVENTS } from "@Utils/webApputils/analyticEvents";

/** Edge of the app heart, in px. Shared with the burst, which sizes its box from it. */
const APP_HEART_SIZE = 23;

const ShortlistButton = ({
  dataTestId,
  inventoryId,
  inventory,
  noAbosolute = false,
  withText = false,
  topRight = false,
  shortlistedCardID,
  className = "",
  wrapperClassName = "",
  screenName = "",
  iconColor,
  appHeart = false,
}: {
  inventoryId: number;
  noAbosolute?: boolean;
  inventory: any;
  withText?: boolean;
  topRight?: boolean;
  dataTestId?: string;
  shortlistedCardID?: any;
  className?: string;
  wrapperClassName?: string;
  screenName?: string;
  iconColor?: string;
  /**
   * Opt in to the native app's heart: the bare overlay heart plus its burst animation,
   * instead of the web heart-in-a-circle. Only honoured inside the app — on the web the
   * button keeps its usual chrome.
   */
  appHeart?: boolean;
}): JSX.Element => {
  const {
    isUserLoggedIn,
    userData,
    shortlistedActivityPage,
    shortlistedCount,
    shortlistedPageCount,
  } = useSelector(
    ({ appData, activityPageData }: AppState) => ({
      isUserLoggedIn: appData.isUserLoggedIn,
      userData: appData.userData,
      shortlistedActivityPage: activityPageData.shortlisted,
      shortlistedCount: activityPageData.shortlistedCount,
      shortlistedPageCount: activityPageData.shortlistedPageCount,
    }),
    shallowEqual,
  );

  const isApp = useIsApp();
  const isNewNativeApp = useIsNewNativeApp();
  const [shortlisted, setShortlisted] = useState({
    isShortlisted: false,
    componentState: "inactive",
  });

  const { t } = useTranslationHook();

  // The app heart's burst, ported from the iOS card. Fired from the settled result of a
  // toggle rather than from the tap, which is the only way to get this right: a logged-out
  // tap opens the login modal without wishlisting anything, and a failed request leaves the
  // state where it was. Bursting on the tap itself would celebrate both of those, and
  // bursting on the state alone would celebrate the `userData` sync that marks a card
  // already wishlisted when it scrolls in.
  const showAppHeart = appHeart && isApp;
  const [burst, setBurst] = useState<BurstHeartAction | null>(null);
  // Separate from `burst`: lottie-web is a dynamic import, so there is a gap between deciding
  // to animate and having something on screen. Hiding the heart on the decision left the
  // button empty for the length of that download on the first wishlist of a session.
  const [burstShowing, setBurstShowing] = useState(false);

  const endBurst = () => {
    setBurst(null);
    setBurstShowing(false);
  };

  /**
   * Which heart to draw while a burst is pending — the pose the animation *starts* from, not
   * the one it ends on.
   *
   * The wishlist state flips as soon as the request succeeds, but the animation cannot start
   * until lottie-web has loaded. Drawing the new pose in that gap made the heart change state
   * instantly and then play an animation of the change it had already shown — which reads as
   * no animation at all on the way out, where the burst is an implosion rather than a
   * scattering and the end pose is the only tell. Holding the old pose until the animation
   * takes over means the two always meet on the same frame.
   */
  const heartPose = burst ? burst === "unlike" : shortlisted.isShortlisted;

  useEffect(() => {
    if (userData) {
      const isListed = userData?.data?.inventories_shortlists_ids.includes(inventoryId);
      setShortlisted({ isShortlisted: isListed, componentState: isListed ? "active" : "inactive" });
    }
  }, [JSON.stringify(userData), inventoryId]);

  const dispatch = useDispatch();
  const activityPageDispatcher = new ActivityPageDispatcher(dispatch);
  // maintaining two different functions here because shortlisting fires more events
  const shortlistInventoryHandler: MouseEventHandler = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isUserLoggedIn) {
      setShortlisted({ isShortlisted: shortlisted.isShortlisted, componentState: "loading" });
      const options = {
        url_path: "/api/v0/auth/users/inventories_shortlists/",
        data: {
          inventories_shortlist: { inventory_id: inventoryId },
        },
      };
      const res = await createInventoriesShortlist(options);
      if (res) {
        // update userdata as soon as shortlist is done
        let tempUserData: any = JSON.stringify(userData);
        tempUserData = JSON.parse(tempUserData);
        tempUserData.data.inventories_shortlists_ids = [
          ...tempUserData.data.inventories_shortlists_ids,
          inventoryId,
        ];
        dispatch(
          updateUserData({
            status: true,
            data: tempUserData,
          }),
        );
        if (shortlistedActivityPage) {
          let updatedshortlistedActivityPage: any = [];

          if (inventory) updatedshortlistedActivityPage = [inventory, ...shortlistedActivityPage];
          activityPageDispatcher.updateShortlistedList(updatedshortlistedActivityPage);
        }
        setShortlisted({ isShortlisted: true, componentState: "active" });
        if (showAppHeart) setBurst("like");
        const toastNotificationDispatcher = new ToastNotificationDispatcher(dispatch);
        toastNotificationDispatcher.openToast({
          title: t(
            isNewNativeApp
              ? "propertypage.propertyheader.wishlist.addedtitle"
              : "propertypage.propertyheader.shortlist.addedtitle",
          ),
          message: t(
            isNewNativeApp
              ? "propertypage.propertyheader.wishlist.addedmessage"
              : "propertypage.propertyheader.shortlist.addedmessage",
          ),
        });
      }
    } else {
      addShortlistParamInURL(inventoryId);
      const loginDispatcher = new LoginDispatcher(dispatch);
      loginDispatcher.showLoginModal({
        openingSource: "shortlist",
        ...(isApp && { screenName }),
      });
    }
    // @ts-ignore
    if (typeof fbq !== "undefined" && fbq !== null) {
      // place shortlist event
      // @ts-ignore
      fbq("track", "place_shortlist", { inventory_id: inventoryId });
    }
    gtagEventEmitter({
      action: "place_shortlist",
      category: "search",
      label: "success",
    });
    logAppEvent({
      eventName: APP_EVENTS.PROPERTY_WISHLISTED,
      payload: {
        property_id: inventory?.id,
        property_name: inventory?.name,
        currency: getPricingCurrencyStandardName(inventory?.pricing?.currency),
        value: getAvailableStartingPricingHelper(inventory?.pricing),
        page: "search",
      },
    });
  };

  const removeShortlistInventoryHandler: MouseEventHandler = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUserLoggedIn) {
      setShortlisted({ isShortlisted: shortlisted.isShortlisted, componentState: "loading" });
      const options = {
        url_path: "/api/v0/auth/users/inventories_shortlists/",
        data: {
          inventories_shortlist: { inventory_id: inventoryId },
        },
      };
      const res = await deleteInventoriesShortlist(inventoryId, options);
      if (res) {
        // update userdata as soon as shortlist is done
        const tempUserDataStringified: string = JSON.stringify(userData);
        let tempUserData = JSON.parse(tempUserDataStringified);
        const index = tempUserData.data.inventories_shortlists_ids.indexOf(inventoryId);
        if (index > -1) {
          tempUserData = {
            ...tempUserData,
            data: {
              ...tempUserData?.data,
              inventories_shortlists_ids: tempUserData?.data?.inventories_shortlists_ids?.filter(
                (id: any) => id !== inventoryId,
              ),
            },
          };
        }
        dispatch(
          updateUserData({
            status: true,
            data: tempUserData,
          }),
        );
        if (shortlistedActivityPage) {
          let updatedshortlistedActivityPage = [];
          updatedshortlistedActivityPage = shortlistedActivityPage.filter(
            (inv: any) => inv.id !== inventoryId,
          );
          activityPageDispatcher.updateShortlistedList(updatedshortlistedActivityPage);
        }

        setShortlisted({ isShortlisted: false, componentState: "inactive" });
        if (showAppHeart) setBurst("unlike");
      }
      const toastNotificationDispatcher = new ToastNotificationDispatcher(dispatch);
      toastNotificationDispatcher.openToast({
        title: t(
          isNewNativeApp
            ? "propertypage.propertyheader.wishlist.removedtitle"
            : "propertypage.propertyheader.shortlist.removedtitle",
        ),
        message: t(
          isNewNativeApp
            ? "propertypage.propertyheader.wishlist.removedmessage"
            : "propertypage.propertyheader.shortlist.removedmessage",
        ),
      });
      // @ts-ignore
      gtagEventEmitter({
        action: "remove_shortlist",
        category: "search",
        label: "success",
      });
    } else {
      const loginDispatcher = new LoginDispatcher(dispatch);
      loginDispatcher.showLoginModal({ openingSource: "shortlist", ...(isApp && { screenName }) });
    }
  };

  const removeShortlistInventoryHandlerForShortlistPage: MouseEventHandler = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUserLoggedIn) {
      setShortlisted({ isShortlisted: shortlisted.isShortlisted, componentState: "loading" });
      const options = {
        url_path: "/api/v0/auth/users/inventories_shortlists/",
        data: {
          inventories_shortlist: { inventory_id: inventoryId },
        },
      };
      const res = await deleteInventoriesShortlist(inventoryId, options);
      if (res) {
        // update userdata as soon as shortlist is done
        const tempUserDataStringified: string = JSON.stringify(userData);
        let tempUserData = JSON.parse(tempUserDataStringified);
        const index = tempUserData.data.inventories_shortlists_ids.indexOf(inventoryId);
        if (index > -1) {
          tempUserData = {
            ...tempUserData,
            data: {
              ...tempUserData?.data,
              inventories_shortlists_ids: tempUserData?.data?.inventories_shortlists_ids?.filter(
                (id: any) => id !== inventoryId,
              ),
            },
          };
        }
        dispatch(
          updateUserData({
            status: true,
            data: tempUserData,
          }),
        );
        if (shortlistedActivityPage) {
          let updatedshortlistedActivityPage = [];
          updatedshortlistedActivityPage = shortlistedActivityPage.filter(
            (inv: any) => inv.id !== shortlistedCardID,
          );
          activityPageDispatcher.updateShortlistedList(updatedshortlistedActivityPage);
          activityPageDispatcher.updateShortlistedListCount(shortlistedCount - 1);
          if ((shortlistedCount - 1) % 10 === 0) {
            activityPageDispatcher.updateShortlistedPageCount(shortlistedPageCount - 1);
          }
        }
        if (showAppHeart) setBurst("unlike");
      }
      const toastNotificationDispatcher = new ToastNotificationDispatcher(dispatch);
      toastNotificationDispatcher.openToast({
        title: t(
          isNewNativeApp
            ? "propertypage.propertyheader.wishlist.removedtitle"
            : "propertypage.propertyheader.shortlist.removedtitle",
        ),
        message: t(
          isNewNativeApp
            ? "propertypage.propertyheader.wishlist.removedmessage"
            : "propertypage.propertyheader.shortlist.removedmessage",
        ),
      });
      // @ts-ignore
      gtagEventEmitter({
        action: "remove_shortlist",
        category: "search",
        label: "success",
      });
    } else {
      const loginDispatcher = new LoginDispatcher(dispatch);
      loginDispatcher.showLoginModal({ openingSource: "shortlist", ...(isApp && { screenName }) });
    }
  };
  const handleAction = (e: any) => {
    if (e.type === "keydown") {
      if (e.key !== "Enter" && e.key !== " ") {
        return;
      }
    }

    if (shortlistedCardID === 0) {
      if (shortlisted.isShortlisted) {
        removeShortlistInventoryHandler(e);
      } else {
        shortlistInventoryHandler(e);
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (shortlisted.isShortlisted) {
        removeShortlistInventoryHandlerForShortlistPage(e);
      } else {
        shortlistInventoryHandler(e);
      }
    }
  };

  return (
    <div
      className={formatClassNames(styles.container, wrapperClassName)}
      role="button"
      tabIndex={0}
      onKeyDown={(e: any) => handleAction(e)}
      onClick={(e: any) => handleAction(e)}
    >
      <div
        className={formatClassNames(
          styles.shortlistButton,
          styles[shortlisted.componentState],
          noAbosolute && styles.noAbosolute,
          withText && styles.withText,
          topRight && styles.topRight,
          showAppHeart && styles.appHeart,
          className && className,
        )}
        data-testid={dataTestId}
      >
        {showAppHeart ? (
          <>
            {/* Hidden only once the burst is actually drawing, so the animation's own heart
                is the only one on screen and there is never a frame with neither. No loader
                here: the app heart has no circle to hold one, and the burst is the feedback. */}
            <span className={burstShowing ? styles.heartHidden : undefined}>
              <WishlistHeartIcon size={APP_HEART_SIZE} wishlisted={heartPose} />
            </span>
            {burst && (
              <BurstHeart
                action={burst}
                heartSize={APP_HEART_SIZE}
                onReady={() => setBurstShowing(true)}
                onFinished={endBurst}
              />
            )}
          </>
        ) : (
          <>
            {shortlisted.componentState === "loading" ? (
              <LoaderIcon />
            ) : (
              <ShortlistIcon
                fill={shortlisted.isShortlisted ? "white" : iconColor || themeVariables.neutral5}
              />
            )}
          </>
        )}
      </div>
      {withText && (
        <div className={styles.text}>
          {shortlisted.isShortlisted
            ? t(
                isNewNativeApp
                  ? "propertypage.propertyheader.wishlist.wishlisted"
                  : "propertypage.propertyheader.shortlist.shortlisted",
              )
            : t(
                isNewNativeApp
                  ? "propertypage.propertyheader.wishlist.wishlist"
                  : "propertypage.propertyheader.shortlist.shortlist",
              )}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(ShortlistButton, {
  componentName: "ShortlistButton-SearchListItemDesktop-SearchPage",
  showForChina: true,
});
